import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { FeaturesSpecsParams } from 'site-modules/shared/utils/features-specs/params';
import { FeatureSpecsEntities } from 'client/data/models/vehicle-v2';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import {
  checkIsOldSafetyFormat,
  getFormattedFeature,
  getSectionAnchor,
} from 'site-modules/features-specs/utils/features-specs';
import { EngineTypeBadge } from 'site-modules/shared/components/engine-type-badge/engine-type-badge';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { TableDataRow } from './table-data-row';

import './features-section.scss';

const PREVIEW_FEATURES_NUMBER = 3;

export function FeaturesSection({
  className = null,
  params,
  style,
  features,
  category,
  showBreadcrumbs = false,
  isFirstSection = false,
  keyword = null,
}) {
  const isOldSafetyFormat = checkIsOldSafetyFormat(params.year);
  const formattedFeatures = features
    .map(feature => getFormattedFeature({ style, feature, category, isOldSafetyFormat }))
    .filter(value => value);

  if (!get(formattedFeatures, 'length')) {
    return null;
  }

  const { hash: sectionId, name } = getSectionAnchor(category, style?.styleAttributes?.electric);
  const { make, model, year } = params;
  const tableCaption = showBreadcrumbs ? `${year} ${make} ${model} > Specs > ${name}` : name;

  const previewFeatures = isFirstSection ? formattedFeatures : formattedFeatures.slice(0, PREVIEW_FEATURES_NUMBER);
  const otherFeatures = isFirstSection ? null : formattedFeatures.slice(PREVIEW_FEATURES_NUMBER);

  return (
    <ScrollElement
      className={classnames(
        'features-section bg-cool-gray-90 rounded-12 px-1 px-md-1_5 py-1_5 mb-1 mb-md-1_5',
        className
      )}
      id={sectionId}
      data-tracking-parent={`features_specs_${tableCaption.toLowerCase().replaceAll(' ', '_')}`}
    >
      <div className="mb-1">
        <h2 className="heading-4 d-inline-block mb-0">{tableCaption}</h2>
        {isFirstSection && <EngineTypeBadge styleFeatures={style} elementStyle={{ marginLeft: '1rem' }} />}
      </div>

      <div className="bg-white rounded-8 px-0_75 px-md-1 py-0_5">
        <table className={classnames('table mb-0', { 'with-other': !!otherFeatures?.length })}>
          <colgroup className="vehicle-columns">
            <col className="feature" />
            <col className="value" />
          </colgroup>
          <tbody>
            {previewFeatures.map((feature, index) => {
              const key = `${category}-${feature.name}-${index}`;

              return <TableDataRow key={key} feature={feature} category={category} keyword={keyword} />;
            })}
          </tbody>
        </table>
        {!!otherFeatures?.length && (
          <Fragment>
            <ContentCollapse
              btnContainerClasses="py-0_75 px-0_5"
              btnClasses="p-0 text-blue-40 text-underline fw-normal"
              btnSize="lg"
              btnArrowClasses="xsmall"
              justify="justify-content-start"
              textClosed="Show more"
              textOpen="Show less"
              btnTrackingId="expand_specs_module"
              isOnlyExpandTracking
              isSameExpandCollapseTracking
              ariaLabelHeading={`about ${tableCaption}`}
              showBtnBelow
              resetParams={
                otherFeatures?.length > PREVIEW_FEATURES_NUMBER * 2
                  ? {
                      duration: SCROLL_DURATION,
                      offset: SCROLL_OFFSET,
                      to: sectionId,
                    }
                  : undefined
              }
            >
              <table className="table mb-0">
                <colgroup className="vehicle-columns">
                  <col className="feature" />
                  <col className="value" />
                </colgroup>
                <tbody>
                  {otherFeatures.map((feature, index) => {
                    const key = `${category}-${feature.name}-${index}`;

                    return <TableDataRow key={key} feature={feature} category={category} keyword={keyword} />;
                  })}
                </tbody>
              </table>
            </ContentCollapse>
          </Fragment>
        )}
      </div>
    </ScrollElement>
  );
}

FeaturesSection.propTypes = {
  params: FeaturesSpecsParams.isRequired,
  style: FeatureSpecsEntities.FeatureStyleEntity.isRequired,
  features: PropTypes.arrayOf(FeatureSpecsEntities.Feature).isRequired,
  category: PropTypes.string.isRequired,
  className: PropTypes.string,
  showBreadcrumbs: PropTypes.bool,
  keyword: PropTypes.string,
  isFirstSection: PropTypes.bool,
};
